'use client';

import React, { useEffect, useRef } from 'react';

export default function VenueSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); }); },
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-muted/40 w-full px-4 md:px-12 pt-12 md:pt-16 pb-16 md:pb-20">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full border border-border text-muted-foreground text-xs font-bold tracking-widest uppercase mb-4 md:mb-5">
            Venues
          </span>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-black text-foreground tracking-tight">
            Where Prathaa Happens
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {/* Main Puja Venue */}
          <div className="reveal bg-card border border-border rounded-3xl md:rounded-4xl p-6 md:p-8 hover:shadow-gold transition-all duration-300">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-secondary/15 flex items-center justify-center text-xl md:text-2xl mb-4 md:mb-6">🪔</div>
            <h3 className="font-display text-xl md:text-2xl font-black text-foreground tracking-tight mb-2">Main Puja Venue</h3>
            <p className="text-muted-foreground text-sm mb-4 md:mb-5">Durga Puja Arena · Oct 16–21, 2026</p>
            <div className="space-y-3 text-sm text-foreground/80">
              <div className="flex items-start gap-3">
                <svg className="text-accent flex-shrink-0 mt-0.5" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                <div>
                  <p className="font-semibold">SNDT Women&apos;s College</p>
                  <p className="text-muted-foreground">R.A. Kidwai Marg, Antop Hill, Mumbai</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="text-accent flex-shrink-0 mt-0.5" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                <div>
                  <p className="font-semibold">6-Day Sponsor Visibility Window</p>
                  <p className="text-muted-foreground">Shashthi (16 Oct) to Dashami (21 Oct)</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="text-accent flex-shrink-0 mt-0.5" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                <div>
                  <p className="font-semibold">10,000+ Footfall (2025)</p>
                  <p className="text-muted-foreground">Across 5 days</p>
                </div>
              </div>
            </div>
            <div className="mt-5 md:mt-6 pt-5 md:pt-6 border-t border-border">
              <p className="text-xs text-muted-foreground">Third year in South-Central Mumbai. Families from Parel, Lower Parel, Dadar, Matunga, Sion, Wadala, and Worli.</p>
            </div>
          </div>

          {/* Gala Venue */}
          <div className="reveal reveal-delay-200 bg-dark-bg border border-accent/20 rounded-3xl md:rounded-4xl p-6 md:p-8 hover:shadow-gold transition-all duration-300">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent/15 flex items-center justify-center text-xl md:text-2xl mb-4 md:mb-6">🎵</div>
            <h3 className="font-display text-xl md:text-2xl font-black text-white tracking-tight mb-2">Gala Evening Venue</h3>
            <p className="text-accent/70 text-sm mb-4 md:mb-5">Tuesday, 20 October 2026 · 7 PM onwards</p>
            <div className="space-y-3 text-sm text-white/80">
              <div className="flex items-start gap-3">
                <svg className="text-accent flex-shrink-0 mt-0.5" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                <div>
                  <p className="font-semibold text-white">Shanmukhananda Chandrasekhara Saraswati Auditorium</p>
                  <p className="text-white/50">Harbanslal Marg, Sion East, Mumbai</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="text-accent flex-shrink-0 mt-0.5" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6"/>
                </svg>
                <div>
                  <p className="font-semibold text-white">~1 km from Kings Circle Station</p>
                  <p className="text-white/50">Central & Harbour railway lines</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="text-accent flex-shrink-0 mt-0.5" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                </svg>
                <div>
                  <p className="font-semibold text-white">2,763 Seated Capacity</p>
                  <p className="text-white/50">Across 3 floors · Exclusive to members</p>
                </div>
              </div>
            </div>
            <div className="mt-5 md:mt-6 pt-5 md:pt-6 border-t border-white/10">
              <p className="text-xs text-white/40">Reach extends to Virar, Navi Mumbai, and Palghar for drive-in Gala audience.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}